import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import { LanguageProvider } from "./lib/language";
import { CartProvider } from "./lib/cart";
import { ThemeProvider } from "./lib/theme";
import { useEffect, useState } from "react";
import axios from "axios";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import GalleriesPage from "./pages/GalleriesPage";
import CreateGalleryPage from "./pages/CreateGalleryPage";
import GalleryDetailPage from "./pages/GalleryDetailPage";
import PublicGalleryPage from "./pages/PublicGalleryPage";
import SettingsPage from "./pages/SettingsPage";
import RetouchRequestsPage from "./pages/RetouchRequestsPage";
import AdminPage from "./pages/AdminPage";
import BookingsPage from "./pages/BookingsPage";
import PublicBookingPage from "./pages/PublicBookingPage";
import PhotographerPortfolioPage from "./pages/PhotographerPortfolioPage";
import ClientsPage from "./pages/ClientsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import OrdersPage from "./pages/OrdersPage";
import ContractsPage from "./pages/ContractsPage";
import WorkflowsPage from "./pages/WorkflowsPage";
import CopilotPage from "./pages/CopilotPage";
import ConfigPage from "./pages/ConfigPage";
import LiveEventPage from "./pages/LiveEventPage";
import ProofingSessionPage from "./pages/ProofingSessionPage";
import MessagesPage from "./pages/MessagesPage";
import BillingPage from "./pages/BillingPage";
import WeddingPlannerPage from "./pages/WeddingPlannerPage";
import PublicWeddingPage from "./pages/PublicWeddingPage";
import AlbumDesignerPage from "./pages/AlbumDesignerPage";
import LabsPage from "./pages/LabsPage";
import ComparisonsPage from "./pages/ComparisonsPage";
import PublicComparePage from "./pages/PublicComparePage";
import PublicOfferPage from "./pages/PublicOfferPage";
import CloudPage from "./pages/CloudPage";
import HelpPage from "./pages/HelpPage";
// Sport module
import SportSeasonsPage from "./pages/SportSeasonsPage";
import SportSeasonDetailPage from "./pages/SportSeasonDetailPage";
import SportEventPage from "./pages/SportEventPage";
import SportDogPage from "./pages/SportDogPage";
import SportTeamPage from "./pages/SportTeamPage";
import SportCaptainPage from "./pages/SportCaptainPage";
import SportAdminPage from "./pages/SportAdminPage";
import SportOrderPage from "./pages/SportOrderPage";
import SportCartPage from "./pages/SportCartPage";
import SsoCallbackPage from "./pages/SsoCallbackPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Access Code Redirect Component
const AccessCodeRedirect = () => {
  const { accessCode } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/c/${accessCode}`);
        navigate(`/g/${response.data.public_id}`, { replace: true });
      } catch (err) {
        setError('Nieprawidłowy kod dostępu');
      }
    };
    fetchGallery();
  }, [accessCode, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Błąd</h2>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
};

// Client Token Redirect Component (QR without password)
const ClientTokenRedirect = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/t/${token}`);
        // Store token in sessionStorage for password bypass
        sessionStorage.setItem(`gallery_token_${response.data.public_id}`, token);
        navigate(`/g/${response.data.public_id}`, { replace: true });
      } catch (err) {
        setError('Nieprawidłowy lub wygasły link');
      }
    };
    fetchGallery();
  }, [token, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Błąd</h2>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    // Redirect admin to admin panel, others to dashboard
    return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/callback" element={<SsoCallbackPage />} />
      
      {/* Public Gallery View */}
      <Route path="/g/:publicId" element={<PublicGalleryPage />} />
      <Route path="/gallery/:publicId" element={<PublicGalleryPage />} />
      <Route path="/c/:accessCode" element={<AccessCodeRedirect />} />
      <Route path="/t/:token" element={<ClientTokenRedirect />} />
      
      {/* Photographer Portfolio */}
      <Route path="/p/:subdomain" element={<PhotographerPortfolioPage />} />
      <Route path="/photographer/:subdomain" element={<PhotographerPortfolioPage />} />
      
      {/* Public Booking Page */}
      <Route path="/booking/:subdomain" element={<PublicBookingPage />} />
      <Route path="/b/:subdomain" element={<PublicBookingPage />} />

      {/* Public Wedding Page */}
      <Route path="/wedding/:weddingId" element={<PublicWeddingPage />} />

      {/* Public Before/After Comparison */}
      <Route path="/compare/:slug" element={<PublicComparePage />} />

      {/* Public Offer Page */}
      <Route path="/offer/:subdomain/:slug" element={<PublicOfferPage />} />

      {/* Public Sport Pages */}
      <Route path="/sport" element={<SportSeasonsPage />} />
      <Route path="/sport/season/:seasonId" element={<SportSeasonDetailPage />} />
      <Route path="/sport/event/:eventId" element={<SportEventPage />} />
      <Route path="/sport/dog/:dogId" element={<SportDogPage />} />
      <Route path="/sport/team/:teamId" element={<SportTeamPage />} />
      <Route path="/sport/captain/:token" element={<SportCaptainPage />} />
      <Route path="/sport/cart" element={<SportCartPage />} />
      <Route path="/sport/order/:orderId/success" element={<SportOrderPage />} />
      <Route path="/sport/order/:orderId/cancel" element={<SportOrderPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/galleries" element={<ProtectedRoute><GalleriesPage /></ProtectedRoute>} />
      <Route path="/galleries/new" element={<ProtectedRoute><CreateGalleryPage /></ProtectedRoute>} />
      <Route path="/galleries/:galleryId" element={<ProtectedRoute><GalleryDetailPage /></ProtectedRoute>} />
      <Route path="/retouch-requests" element={<ProtectedRoute><RetouchRequestsPage /></ProtectedRoute>} />
      <Route path="/bookings" element={<ProtectedRoute><BookingsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
      <Route path="/clients/:clientId" element={<ProtectedRoute><ClientDetailPage /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
      <Route path="/contracts" element={<ProtectedRoute><ContractsPage /></ProtectedRoute>} />
      <Route path="/workflows" element={<ProtectedRoute><WorkflowsPage /></ProtectedRoute>} />
      <Route path="/ai" element={<ProtectedRoute><CopilotPage /></ProtectedRoute>} />
      <Route path="/config" element={<ProtectedRoute><ConfigPage /></ProtectedRoute>} />
      <Route path="/live-events" element={<ProtectedRoute><LiveEventPage /></ProtectedRoute>} />
      <Route path="/proofing" element={<ProtectedRoute><ProofingSessionPage /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/weddings" element={<ProtectedRoute><WeddingPlannerPage /></ProtectedRoute>} />
      <Route path="/albums" element={<ProtectedRoute><AlbumDesignerPage /></ProtectedRoute>} />
      <Route path="/labs" element={<ProtectedRoute><LabsPage /></ProtectedRoute>} />
      <Route path="/comparisons" element={<ProtectedRoute><ComparisonsPage /></ProtectedRoute>} />
      <Route path="/cloud" element={<ProtectedRoute><CloudPage /></ProtectedRoute>} />
      <Route path="/sport/admin" element={<ProtectedRoute><SportAdminPage /></ProtectedRoute>} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
